import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useSearchParams, Link, useNavigate, useLocation } from "react-router-dom";
import GuestOverlay from "@/components/auth/GuestOverlay";
import Navbar from "@/components/layout/Navbar";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/hooks/useAuth";
import { useGeneration } from "@/hooks/useGeneration";
import { supabase } from "@/integrations/supabase/client";
import { checkTaskStatus } from "@/lib/task-checker";
import { Progress } from "@/components/ui/progress";
import {
  Image,
  Video,
  Music,
  Wand2,
  Upload,
  Sparkles,
  Loader2,
  Download,
  Coins,
  AlertTriangle,
  CheckCircle2,
  History,
  Plus,
  Repeat,
  X,
  Crown,
  Eraser,
  FastForward,
  Clock,
  RefreshCw,
  Trash2,
  Pencil,
  ArrowRight
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useToast } from "@/hooks/use-toast";
import { ToastAction } from "@/components/ui/toast";
import VideoDanceTab from "@/components/generate/VideoDanceTab";
import RecentUploads from "@/components/generate/RecentUploads";

const MAGIC_EXTRA_CREDITS = 5;

const Generate = () => {
  const { type: typeParam } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation(); // Used for receiving state
  const defaultTab = typeParam || searchParams.get("type") || "photo";
  const promptFromUrl = searchParams.get("prompt") || "";
  const { t } = useLanguage();
  const { user, loading: authLoading, profile } = useAuth();
  const { toast } = useToast();
  const { generate, isGenerating, generationProgress, statusMessage, userCredits } = useGeneration();

  const photoModels = [
    { id: "gpt-image", name: "GPT Image", credits: 4, supportsImage: true },
    { id: "5-lite", name: "5 Lite", credits: 5, supportsImage: false },
    { id: "seedream", name: "Seedream 4.5", credits: 6.5, supportsImage: true },
    { id: "nanobana", name: "Nano Banana", credits: 4, supportsImage: true },
    { id: "nanobana-pro", name: "Nano Banana Pro", credits: 18, supportsImage: true },
  ];

  const videoModels = [
    // Veo 3.1 Lite - most economical, default
    { id: "veo31-lite", name: "Veo 3.1 Lite (8s)", credits: 30, supportsImage: true, duration: "8s", veoModel: "veo3_lite" },
    // Veo 3.1 Fast - higher quality
    { id: "veo31-fast", name: "Veo 3.1 Fast (8s)", credits: 60, supportsImage: true, duration: "8s", veoModel: "veo3_fast" },
    { id: "wan26-5s", name: "Wan 2.6 (5s)", credits: 70, credits1080: 105, supportsImage: false, duration: "5s", wanDuration: "5" },
    { id: "wan26-10s", name: "Wan 2.6 (10s)", credits: 140, credits1080: 210, supportsImage: false, duration: "10s", wanDuration: "10" },
    { id: "wan26-15s", name: "Wan 2.6 (15s)", credits: 210, credits1080: 315, supportsImage: false, duration: "15s", wanDuration: "15" },
  ];

  const [activeTab, setActiveTab] = useState(defaultTab);
  const [promptText, setPromptText] = useState(promptFromUrl);
  const [selectedPhotoModel, setSelectedPhotoModel] = useState("seedream");

  // Get model from URL parameter, validate it exists, default to veo31-fast
  const modelFromUrl = searchParams.get("model");
  const validVideoModelIds = videoModels.map(m => m.id);
  const defaultVideoModel = modelFromUrl && validVideoModelIds.includes(modelFromUrl) ? modelFromUrl : "veo31-lite";
  const [selectedVideoModel, setSelectedVideoModel] = useState(defaultVideoModel);
  const [selectedRatio, setSelectedRatio] = useState("9:16");
  const [wanResolution, setWanResolution] = useState<"720p" | "1080p">("720p");
  const [veoFullHD, setVeoFullHD] = useState(false);
  const [isInstrumental, setIsInstrumental] = useState(false);
  const [lyricsText, setLyricsText] = useState("");
  const [referenceImage, setReferenceImage] = useState<File | null>(null);
  const [referenceImages, setReferenceImages] = useState<File[]>([]); // Multiple images for Nano Banana Pro
  const [magicLoading, setMagicLoading] = useState(false);
  const isSoraVideo = false; // Sora2 discontinued Apr 26, 2026
  const videoAspectOptions = [
    { value: "9:16", label: t("แนวตั้ง", "Portrait") },
    { value: "16:9", label: t("แนวนอน", "Landscape") },
  ];

  // Nano Banana / Nano Banana Pro aspect ratios (per API documentation)
  const nanoBananaAspectRatios = [
    { value: "1:1", label: "1:1" },
    { value: "2:3", label: "2:3" },
    { value: "3:2", label: "3:2" },
    { value: "3:4", label: "3:4" },
    { value: "4:3", label: "4:3" },
    { value: "4:5", label: "4:5" },
    { value: "5:4", label: "5:4" },
    { value: "9:16", label: "9:16" },
    { value: "16:9", label: "16:9" },
    { value: "21:9", label: "21:9" },
    { value: "auto", label: t("อัตโนมัติ", "Auto") },
  ];

  // Check if current model is Nano Banana
  const isNanoBananaModel = selectedPhotoModel === "nanobana" || selectedPhotoModel === "nanobana-pro";

  // Check if current model is a Market model (Seedream / 5 Lite)
  const isMarketModel = selectedPhotoModel === "seedream" || selectedPhotoModel === "5-lite";

  const currentPhotoModel = photoModels.find(m => m.id === selectedPhotoModel);
  const supportsImage = currentPhotoModel?.supportsImage ?? true;

  // Check for prompt in state (e.g. from Reuse button)
  useEffect(() => {
    if (location.state?.prompt) {
      setPromptText(location.state.prompt);
    }
  }, [location.state]);

  // Update ratio when model changes to gpt-image
  useEffect(() => {
    if (selectedPhotoModel === "gpt-image") {
      setSelectedRatio("1024x1024");
    } else if (selectedRatio === "1024x1024") {
      setSelectedRatio("9:16");
    }
  }, [selectedPhotoModel, selectedRatio]);

  useEffect(() => {
    if (activeTab === "video") {
      const allowedRatios = ["9:16", "16:9"];
      if (!allowedRatios.includes(selectedRatio)) {
        setSelectedRatio("9:16");
      }
    }
  }, [activeTab, selectedRatio]);

  useEffect(() => {
    if (typeParam && typeParam !== activeTab) {
      setActiveTab(typeParam);
    }
  }, [typeParam]);

  const handleTabChange = (val: string) => {
    setActiveTab(val);
    navigate(`/generate/${val}`, { replace: true });
  };
  const [generatedResult, setGeneratedResult] = useState<string | null>(null);
  const [lastGeneration, setLastGeneration] = useState<{ type: string; model: string; credits: number } | null>(null);
  const [showContinueButton, setShowContinueButton] = useState(false);
  const [allowNewGeneration, setAllowNewGeneration] = useState(false);

  // Recent music state
  interface MusicLog {
    id: string;
    prompt: string;
    display_prompt?: string | null;
    is_professional?: boolean;
    is_magic?: boolean;
    result_url: string | null;
    status: string;
    created_at: string;
    model: string;
    credits_used?: number;
  }

  const [recentMusic, setRecentMusic] = useState<MusicLog[]>([]);
  const [loadingMusic, setLoadingMusic] = useState(false);
  const [recentVideos, setRecentVideos] = useState<MusicLog[]>([]); // Reusing MusicLog interface for simplicity as fields are same
  const [loadingVideos, setLoadingVideos] = useState(false);
  const [recentPhotos, setRecentPhotos] = useState<MusicLog[]>([]);
  const [loadingPhotos, setLoadingPhotos] = useState(false);
  const [recentMotion, setRecentMotion] = useState<MusicLog[]>([]);
  const [loadingMotion, setLoadingMotion] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [checkingStatus, setCheckingStatus] = useState<string | null>(null);

  // Nano Banana Edit state
  const [editingImageUrl, setEditingImageUrl] = useState<string | null>(null);
  const [referenceImageUrl, setReferenceImageUrl] = useState<string | null>(null);
  const [editPrompt, setEditPrompt] = useState("");
  const [isEditing, setIsEditing] = useState(false);

  // Fetch recent music when on music tab
  const fetchRecentMusic = async () => {
    if (!user) return;
    setLoadingMusic(true);
    try {
      const { data, error } = await supabase
        .from("generation_logs")
        .select("id, prompt, display_prompt, is_professional, is_magic, result_url, status, created_at, model, credits_used")
        .eq("user_id", user.id)
        .eq("service_type", "music")
        .order("created_at", { ascending: false })
        .limit(10);

      if (error) throw error;
      setRecentMusic(data as any as MusicLog[]);
    } catch (err) {
      console.error("Error fetching recent music:", err);
    } finally {
      setLoadingMusic(false);
    }
  };

  // Fetch recent videos when on video tab
  const fetchRecentVideos = async () => {
    if (!user) return;
    setLoadingVideos(true);
    try {
      const { data, error } = await supabase
        .from("generation_logs")
        .select("id, prompt, display_prompt, is_professional, is_magic, result_url, status, created_at, model, credits_used")
        .eq("user_id", user.id)
        .eq("service_type", "video")
        .order("created_at", { ascending: false })
        .limit(10);

      if (error) throw error;
      setRecentVideos(data as any as MusicLog[]);
    } catch (err) {
      console.error("Error fetching recent videos:", err);
    } finally {
      setLoadingVideos(false);
    }
  };

  // Fetch recent motion videos when on motion tab
  const fetchRecentMotion = async () => {
    if (!user) return;
    setLoadingMotion(true);
    try {
      const { data, error } = await supabase
        .from("generation_logs")
        .select("id, prompt, display_prompt, is_professional, is_magic, result_url, status, created_at, model, credits_used")
        .eq("user_id", user.id)
        .eq("service_type", "video_motion" as any)
        .order("created_at", { ascending: false })
        .limit(10);

      if (error) throw error;
      setRecentMotion(data as any as MusicLog[]);
    } catch (err) {
      console.error("Error fetching recent motion:", err);
    } finally {
      setLoadingMotion(false);
    }
  };

  // Fetch recent photos when on photo tab
  const fetchRecentPhotos = async () => {
    if (!user) return;
    setLoadingPhotos(true);
    try {
      const { data, error } = await supabase
        .from("generation_logs")
        .select("id, prompt, display_prompt, is_professional, is_magic, result_url, status, created_at, model, credits_used")
        .eq("user_id", user.id)
        .eq("service_type", "photo")
        .order("created_at", { ascending: false })
        .limit(10);

      if (error) throw error;
      setRecentPhotos(data as any as MusicLog[]);
    } catch (err) {
      console.error("Error fetching recent photos:", err);
    } finally {
      setLoadingPhotos(false);
    }
  };

  useEffect(() => {
    if (user) {
      if (activeTab === "music") {
        fetchRecentMusic();
      } else if (activeTab === "video") {
        fetchRecentVideos();
      } else if (activeTab === "photo") {
        fetchRecentPhotos();
      } else if (activeTab === "motion") {
        fetchRecentMotion();
      }
    }
  }, [user, activeTab, generatedResult]);

  // Redirect if not logged in - REMOVED to allow guest preview
  useEffect(() => {
    // if (!authLoading && !user) {
    //   navigate("/login");
    // }
  }, [user, authLoading, navigate]);

  // Scroll to history section on page load if generation was in progress
  useEffect(() => {
    const pendingGeneration = localStorage.getItem('pending_generation_tab');
    if (pendingGeneration && pendingGeneration === activeTab) {
      // Clear the flag immediately
      localStorage.removeItem('pending_generation_tab');
      // Scroll to history section after 500ms to let data load
      setTimeout(() => {
        const historySection = document.getElementById('recent-history-section');
        if (historySection) {
          historySection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 500);
    }
  }, [activeTab]);

  // Realtime subscription for callback notifications
  const subscriptionRef = useRef<any>(null);
  const processedCallbacksRef = useRef<Set<string>>(new Set());

  const handleRealtimeUpdate = useCallback((payload: any) => {
    if (!payload?.new) return;
    const updated = payload.new;
    const old = payload.old;

    // Only process if status changed to completed and there's a result_url
    if (updated.status === 'completed' &&
      updated.result_url &&
      updated.result_url.startsWith('http') &&
      old?.status !== 'completed') {
      // Check if we already processed this callback
      if (processedCallbacksRef.current.has(updated.id)) return;
      processedCallbacksRef.current.add(updated.id);

      // Clean up old processed IDs (keep only last 20)
      if (processedCallbacksRef.current.size > 20) {
        const arr = Array.from(processedCallbacksRef.current);
        processedCallbacksRef.current = new Set(arr.slice(-10));
      }

      // Show notification popup
      const serviceType = updated.service_type || 'video';
      const title = {
        video: t("🎬 วิดีโอพร้อมแล้ว!", "🎬 Video Ready!"),
        music: t("🎵 เพลงพร้อมแล้ว!", "🎵 Music Ready!"),
        photo: t("🖼️ รูปพร้อมแล้ว!", "🖼️ Photo Ready!")
      }[serviceType] || t("✨ งานเสร็จแล้ว!", "✨ Generation Complete!");

      toast({
        title: title,
        description: t("คลิกเพื่อดูผลลัพธ์", "Click to view result"),
        action: (
          <ToastAction
            altText={t("ดูประวัติ", "View history")}
            onClick={() => navigate("/history")}
          >
            {t("ดูประวัติ", "History")}
          </ToastAction>
        ),
        duration: 10000,
      });

      // Refresh the list for current tab
      if (serviceType === 'video' && activeTab === 'video') {
        fetchRecentVideos();
      } else if (serviceType === 'music' && activeTab === 'music') {
        fetchRecentMusic();
      } else if (serviceType === 'photo' && activeTab === 'photo') {
        fetchRecentPhotos();
      }
    }
  }, [activeTab, navigate, t, toast]);

  useEffect(() => {
    if (!user) return;

    // Subscribe to realtime updates on generation_logs for this user
    const channel = supabase.channel('generation-callbacks')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'generation_logs',
          filter: `user_id=eq.${user.id}`
        },
        handleRealtimeUpdate
      )
      .subscribe();

    subscriptionRef.current = channel;

    return () => {
      if (subscriptionRef.current) {
        supabase.removeChannel(subscriptionRef.current);
      }
    };
  }, [user, handleRealtimeUpdate]);


  const getRequiredCredits = () => {
    if (activeTab === "photo") {
      return photoModels.find(m => m.id === selectedPhotoModel)?.credits || 0;
    } else if (activeTab === "video") {
      const model = videoModels.find(m => m.id === selectedVideoModel);
      if (!model) return 0;
      // Use 1080p pricing for Wan models if selected
      const isWanModel = selectedVideoModel.startsWith("wan26");
      if (isWanModel && wanResolution === "1080p" && (model as any).credits1080) {
        return (model as any).credits1080;
      }
      const isVeoModel = selectedVideoModel.startsWith("veo31");
      const veoHDExtra = isVeoModel && veoFullHD ? 5 : 0;
      return model.credits + veoHDExtra;
    } else if (activeTab === "music") {
      return 12;
    }
    return 0;
  };

  const getSelectedModelName = () => {
    if (activeTab === "photo") {
      return photoModels.find(m => m.id === selectedPhotoModel)?.name || "";
    } else if (activeTab === "video") {
      return videoModels.find(m => m.id === selectedVideoModel)?.name || "";
    } else if (activeTab === "music") {
      return "Suno Music";
    }
    return "";
  };

  // Only check credits after auth is loaded to avoid flash of insufficient credits warning
  const hasEnoughCredits = authLoading || userCredits === undefined ? true : userCredits >= getRequiredCredits();
  const hasEnoughMagicCredits = authLoading || userCredits === undefined ? true : userCredits >= getRequiredCredits() + MAGIC_EXTRA_CREDITS;

  const handleGenerationSuccess = (resultUrl: string, modelName: string, credits: number) => {
    setGeneratedResult(resultUrl);
    setLastGeneration({
      type: activeTab,
      model: modelName,
      credits,
    });
    if (activeTab === "video") {
      toast({
        title: t("งานเสร็จแล้ว", "Video ready"),
        description: t("คลิกเพื่อไปดูประวัติ", "Click to open your history"),
        action: (
          <ToastAction altText={t("ดูประวัติ", "View history")} onClick={() => navigate("/history")}>
            {t("ดูประวัติ", "History")}
          </ToastAction>
        ),
      });
    }
  };

  const handleGenerate = async () => {
    if (!promptText.trim()) return;

    const requiredCredits = getRequiredCredits();
    const modelName = getSelectedModelName();

    // Reset states for new generation
    setAllowNewGeneration(false);

    // Show continue button when generation starts (for all types)
    setShowContinueButton(true);
    // Save to localStorage so we can scroll to history on page refresh
    localStorage.setItem('pending_generation_tab', activeTab);

    // Get model-specific options
    const currentVideoModel = videoModels.find(m => m.id === selectedVideoModel);
    const wanDuration = (currentVideoModel as any)?.wanDuration;
    const veoModelVariant = (currentVideoModel as any)?.veoModel;

    const result = await generate(
      activeTab as "photo" | "video" | "music",
      modelName,
      promptText,
      requiredCredits,
      activeTab === "music"
        ? { instrumental: isInstrumental, lyrics: lyricsText }
        : (activeTab === "photo" || activeTab === "video")
          ? {
            ratio: selectedRatio,
            referenceImage: referenceImage || referenceImageUrl,
            referenceImages: selectedPhotoModel === "nanobana-pro" ? referenceImages : undefined,
            wanDuration: wanDuration,
            wanResolution: wanResolution,
            nanobanaPro: selectedPhotoModel === "nanobana-pro",
            veoModel: veoModelVariant,
            veoFullHD: veoFullHD,
          }
          : {}
    );

    // Hide continue button when generation completes
    setShowContinueButton(false);
    // Clear localStorage flag since generation is done (no need to scroll on reload)
    localStorage.removeItem('pending_generation_tab');

    if (result.success && result.resultUrl) {
      handleGenerationSuccess(result.resultUrl, modelName, requiredCredits);
      setReferenceImage(null);
    }

    // Immediately refresh the logs to show the new generation
    if (activeTab === "video") {
      fetchRecentVideos();
    } else if (activeTab === "music") {
      fetchRecentMusic();
    } else if (activeTab === "photo") {
      fetchRecentPhotos();
    }

    // Auto-scroll to history section after 500ms (reduce from 2s for instant feedback)
    setTimeout(() => {
      const historySection = document.getElementById('recent-history-section');
      if (historySection) {
        historySection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 500);
  };

  const fetchRandomMagicPrompt = async (): Promise<{ prompt: string; category?: string | null }> => {
    const { data, error } = await supabase.rpc("get_random_magic_prompt" as any);

    if (error) {
      throw error;
    }

    const row = Array.isArray(data) ? data[0] : data;
    if (!row?.prompt) {
      throw new Error("No magic prompts available");
    }

    return { prompt: row.prompt, category: row.category ?? null };
  };

  const handleMagicGenerate = async () => {
    if (activeTab !== "video") return;
    if (isGenerating && !allowNewGeneration) return;

    const requiredCredits = getRequiredCredits() + MAGIC_EXTRA_CREDITS;
    if (!authLoading && userCredits < requiredCredits) {
      toast({
        variant: "destructive",
        title: t("เครดิตไม่เพียงพอ", "Insufficient Credits"),
        description: t(
          `ต้องใช้ ${requiredCredits} เครดิต (รวม Magic +${MAGIC_EXTRA_CREDITS})`,
          `Need ${requiredCredits} credits (Magic +${MAGIC_EXTRA_CREDITS})`
        ),
      });
      return;
    }

    setMagicLoading(true);
    try {
      const magicPrompt = await fetchRandomMagicPrompt();
      const modelName = getSelectedModelName();

      setAllowNewGeneration(false);
      setShowContinueButton(true);

      // Get model-specific options for magic generation
      const magicVideoModel = videoModels.find(m => m.id === selectedVideoModel);
      const wanDuration = (magicVideoModel as any)?.wanDuration;
      const magicVeoModel = (magicVideoModel as any)?.veoModel;

      const result = await generate(
        "video",
        modelName,
        magicPrompt.prompt,
        requiredCredits,
        {
          ratio: selectedRatio,
          magic: true,
          displayPrompt: "Magic Button",
          magicCategory: magicPrompt.category,
          wanDuration: wanDuration,
          wanResolution: wanResolution,
          veoModel: magicVeoModel,
          veoFullHD: veoFullHD,
        }
      );

      setShowContinueButton(false);

      if (result.success && result.resultUrl) {
        handleGenerationSuccess(result.resultUrl, modelName, requiredCredits);
      }
    } catch (error: any) {
      console.error("Magic generation error:", error);
      toast({
        title: t("เกิดข้อผิดพลาด", "Error"),
        description: error.message || t("ไม่สามารถสุ่มพรอมต์ได้", "Failed to get magic prompt"),
        variant: "destructive",
      });
    } finally {
      setMagicLoading(false);
    }
  };

  const handleContinueWithoutWaiting = () => {
    // Hide the continue button and allow user to start a new generation
    setShowContinueButton(false);
    setAllowNewGeneration(true);
    // Note: The current generation will continue in the background via polling
    // User can check Dashboard or History for results
    toast({
      title: t("สร้างต่อได้เลย!", "You can continue!"),
      description: t("ผลลัพธ์ก่อนหน้าจะเก็บไว้ที่หน้า Dashboard", "Previous result will be saved to Dashboard"),
    });
  };

  const handlePromptSuggestion = async () => {
    // TODO: Implement AI prompt suggestion with credit deduction
    setPromptText("A futuristic cityscape at sunset with neon lights reflecting on wet streets, cyberpunk aesthetic, ultra detailed, 8k");
  };

  const handleReusePrompt = (prompt: string) => {
    setPromptText(prompt);
    window.scrollTo(0, 0);

  };

  const handleAnimatePhoto = (url: string, prompt: string) => {
    setActiveTab("video");
    navigate("/generate/video", { replace: true });
    setSelectedVideoModel("veo31-fast");
    setPromptText(prompt);
    setReferenceImageUrl(url);
    setReferenceImage(null); // Clear file if exists
    window.scrollTo(0, 0);

    toast({
      title: t("⚡ พร้อมสร้างวิดีโอ!", "⚡ Ready to animate!"),
      description: t("เลือกโมเดล Veo 3.1 และสัดส่วนที่ต้องการแล้วกด 'สร้าง' ได้เลย", "Model Veo 3.1 selected. Review prompt and click 'Generate'.")
    });
  };

  // Helper to fix Kie.ai music stream URLs (add .mp3 extension if missing)
  const getMusicAudioUrl = (url: string): string => {
    if (url.includes('musicfile.kie.ai') && !url.includes('.mp3')) {
      return url + '.mp3';
    }
    return url;
  };

  const formatDateHistory = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('th-TH', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const handleDeleteHistory = async (id: string, serviceType: string) => {
    try {
      if (!confirm(t("คุณแน่ใจหรือไม่ที่จะลบรายการนี้?", "Are you sure you want to delete this item?"))) {
        return;
      }

      const { data: success, error } = await supabase.rpc('delete_generation_log' as any, { p_log_id: id });

      if (error) {
        // Fallback: Standard delete
        const { error: dbError } = await supabase
          .from("generation_logs")
          .delete()
          .eq("id", id);

        if (dbError) throw dbError;
      }

      toast({
        title: t("ลบสำเร็จ", "Deleted successfully"),
        description: t("รายการถูกลบออกจากประวัติแล้ว", "The item has been removed from history"),
      });

      // Reload data
      if (serviceType === "video") fetchRecentVideos();
      else if (serviceType === "music") fetchRecentMusic();
      else if (serviceType === "photo") fetchRecentPhotos();
    } catch (error: any) {
      toast({
        title: t("เกิดข้อผิดพลาด", "Error"),
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const getDisplayPrompt = (log: MusicLog): string => {
    if (log.is_magic) {
      return log.display_prompt || t("Magic Button", "Magic Button");
    }
    const isProfessional = log.is_professional || log.model.includes("Professional");
    return isProfessional && log.display_prompt ? log.display_prompt : log.prompt;
  };

  // Get status badge component
  const getStatusBadge = (status: string) => {
    switch (status) {
      case "completed":
        return <Badge className="bg-green-500/10 text-green-500 border-green-500/20 hover:bg-green-500/20">{t("สำเร็จ", "Completed")}</Badge>;
      case "processing":
        return <Badge className="bg-blue-500/10 text-blue-500 border-blue-500/20 animate-pulse">{t("กำลังประมวลผล", "Processing")}</Badge>;
      case "pending":
        return <Badge className="bg-yellow-500/10 text-yellow-500 border-yellow-500/20">{t("รอดำเนินการ", "Pending")}</Badge>;
      case "failed":
        return <Badge variant="destructive" className="bg-red-500/10 text-red-500 border-red-500/20 hover:bg-red-500/20">{t("ล้มเหลว", "Failed")}</Badge>;
      default:
        return null;
    }
  };

  // Handle check status for pending/processing items
  const handleCheckStatus = async (logId: string, serviceType: string) => {
    setCheckingStatus(logId);
    try {
      const result = await checkTaskStatus(logId);

      // Helper to reload data based on service type
      const reloadData = () => {
        if (serviceType === "video") fetchRecentVideos();
        else if (serviceType === "music") fetchRecentMusic();
        else if (serviceType === "photo") fetchRecentPhotos();
        else if (serviceType === "motion" || serviceType === "video_motion") fetchRecentMotion();
      };

      if (result.success && result.status === "completed" && result.url) {
        toast({
          title: t("✅ เสร็จแล้ว!", "✅ Completed!"),
          description: t("ผลลัพธ์พร้อมแล้ว", "Result is ready")
        });
        reloadData();
      } else if (result.status === "processing") {
        toast({
          title: t("⏳ กำลังประมวลผล", "⏳ Processing"),
          description: t("กรุณารอสักครู่", "Please wait a moment")
        });
      } else if (result.status === "failed") {
        toast({
          title: t("❌ ล้มเหลว", "❌ Failed"),
          description: result.message,
          variant: "destructive"
        });
        // Reload data after failure to update UI
        reloadData();
      }
    } catch (error: any) {
      toast({
        title: t("เกิดข้อผิดพลาด", "Error"),
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setCheckingStatus(null);
    }
  };

  const handleDownload = async (url: string, filename: string) => {
    // Detect iOS devices
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

    // For Kie.ai music stream URLs without extension, try the .mp3 version
    let downloadUrl = url;
    if (url.includes('musicfile.kie.ai') && !url.includes('.mp3')) {
      // Try adding .mp3 extension for direct download
      downloadUrl = url + '.mp3';
    }

    if (isIOS) {
      // iOS Safari doesn't support download attribute properly
      // Open in new tab so user can long-press to save
      window.open(downloadUrl, '_blank');
      toast({
        title: t("กดค้างเพื่อบันทึก", "Long-press to save"),
        description: t("iOS ไม่รองรับการดาวน์โหลดโดยตรง", "iOS doesn't support direct download"),
      });
      return;
    }

    try {
      const response = await fetch(downloadUrl, { mode: 'cors' });

      // Check if response is ok
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const blob = await response.blob();

      // Check if blob has content (CORS failures often result in 0-byte blobs)
      if (blob.size === 0) {
        throw new Error('Empty file - CORS blocked');
      }

      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      // Fallback: Try original URL if .mp3 version failed
      if (downloadUrl !== url) {
        try {
          const response = await fetch(url, { mode: 'cors' });
          if (response.ok) {
            const blob = await response.blob();
            if (blob.size > 0) {
              const blobUrl = window.URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = blobUrl;
              link.download = filename;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              window.URL.revokeObjectURL(blobUrl);
              return;
            }
          }
        } catch { }
      }

      // Final fallback: Open in new tab for manual save
      window.open(downloadUrl, '_blank');
    }
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast({
          variant: "destructive",
          title: t("ไฟล์ไม่ถูกต้อง", "Invalid file"),
          description: t("กรุณาอัพโหลดไฟล์รูปภาพเท่านั้น", "Please upload image files only")
        });
        return;
      }
      setReferenceImage(file);
    }
  };

  // Handle multiple image upload for Nano Banana Pro
  const handleMultipleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const validTypes = ['image/jpeg', 'image/png', 'image/webp'];
    const maxSize = 30 * 1024 * 1024; // 30MB

    const validFiles: File[] = [];
    const errors: string[] = [];

    for (const file of files) {
      if (!validTypes.includes(file.type)) {
        errors.push(t(`${file.name}: รูปแบบไฟล์ไม่รองรับ`, `${file.name}: unsupported format`));
        continue;
      }
      if (file.size > maxSize) {
        errors.push(t(`${file.name}: ไฟล์ใหญ่เกิน 30MB`, `${file.name}: exceeds 30MB`));
        continue;
      }
      validFiles.push(file);
    }

    // Calculate how many more images we can add
    const remainingSlots = 8 - referenceImages.length;
    const filesToAdd = validFiles.slice(0, remainingSlots);

    if (validFiles.length > remainingSlots) {
      toast({
        title: t("เพิ่มภาพได้สูงสุด 8 ภาพ", "Maximum 8 images allowed"),
        description: t(`เพิ่มได้อีก ${remainingSlots} ภาพเท่านั้น`, `Can only add ${remainingSlots} more images`),
      });
    }

    if (errors.length > 0) {
      toast({
        variant: "destructive",
        title: t("บางไฟล์ไม่ถูกต้อง", "Some files are invalid"),
        description: errors.slice(0, 3).join(", "),
      });
    }

    if (filesToAdd.length > 0) {
      setReferenceImages(prev => [...prev, ...filesToAdd]);
    }

    // Reset input
    e.target.value = '';
  };

  // Remove a specific image from the reference images array
  const removeReferenceImage = (index: number) => {
    setReferenceImages(prev => prev.filter((_, i) => i !== index));
  };

  // Handle Nano Banana Edit
  const NANO_BANANA_EDIT_CREDITS = 4;

  const handleNanoBananaEdit = async () => {
    if (!editingImageUrl || !editPrompt.trim()) {
      toast({
        variant: "destructive",
        title: t("กรุณาใส่คำอธิบาย", "Please enter a prompt"),
        description: t("บอก AI ว่าต้องการแก้ไขภาพอย่างไร", "Tell AI how you want to edit the image")
      });
      return;
    }

    if (!user || !profile) {
      toast({
        variant: "destructive",
        title: t("กรุณาเข้าสู่ระบบ", "Please log in"),
      });
      return;
    }

    if (profile.credits < NANO_BANANA_EDIT_CREDITS) {
      toast({
        variant: "destructive",
        title: t("เครดิตไม่เพียงพอ", "Insufficient credits"),
        description: t(`ต้องการ ${NANO_BANANA_EDIT_CREDITS} เครดิต`, `Need ${NANO_BANANA_EDIT_CREDITS} credits`)
      });
      return;
    }

    setIsEditing(true);

    try {
      const result = await generate(
        "photo",
        "Nano Banana Edit",
        editPrompt,
        NANO_BANANA_EDIT_CREDITS,
        {
          ratio: selectedRatio,
          nanoBananaEdit: true,
          imageUrls: [editingImageUrl]
        }
      );

      if (result.success) {
        toast({
          title: t("✨ แก้ไขสำเร็จ!", "✨ Edit successful!"),
          description: t("ดูผลลัพธ์ในหน้าประวัติ", "View result in history"),
          action: (
            <ToastAction altText={t("ดูประวัติ", "View history")} onClick={() => navigate("/history")}>
              {t("ดูประวัติ", "History")}
            </ToastAction>
          ),
        });
        fetchRecentPhotos();
        setEditingImageUrl(null);
        setEditPrompt("");
      }
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: t("เกิดข้อผิดพลาด", "Error"),
        description: error.message
      });
    } finally {
      setIsEditing(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }


  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="pt-20 pb-12">
        <div className="container mx-auto px-4">
          {/* Header */}
          <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-heading font-bold mb-2">
                {t("สร้างคอนเทนต์", "Generate Content")}
              </h1>
              <p className="text-muted-foreground">
                {t("สร้างภาพ วิดีโอ และเพลงด้วย AI ที่ล้ำสมัย", "Create amazing AI-generated photos, videos, and music")}
              </p>
            </div>
            {/* Credit Balance Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-primary/10 border border-primary/20 cursor-pointer hover:bg-primary/20 transition-all group">
                  <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center group-hover:bg-primary/30 transition-colors">
                    <Coins className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">{t("เครดิตคงเหลือ", "Remaining Credits")}</p>
                    <p className="text-xl font-bold text-primary">{(userCredits || 0).toLocaleString()}</p>
                  </div>
                </div>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={() => navigate("/history")} className="gap-2 cursor-pointer">
                  <History className="h-4 w-4" />
                  <span>{t("แสดงประวัติ", "Show History")}</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/pricing")} className="gap-2 cursor-pointer">
                  <Plus className="h-4 w-4 text-primary" />
                  <span className="text-primary font-medium">{t("เติมเครดิต", "Top Up")}</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Generation Panel */}
            <div className="lg:col-span-2">
              <Card variant="glass">
                <CardHeader>
                  <Tabs value={activeTab} onValueChange={handleTabChange}>
                    <TabsList className="grid grid-cols-4 w-full max-w-lg">
                      <TabsTrigger value="photo" className="gap-2">
                        <Image className="h-4 w-4" />
                        {t("ภาพ", "Photo")}
                      </TabsTrigger>
                      <TabsTrigger value="video" className="gap-2">
                        <Video className="h-4 w-4" />
                        {t("วิดีโอ", "Video")}
                      </TabsTrigger>
                      <TabsTrigger value="music" className="gap-2">
                        <Music className="h-4 w-4" />
                        {t("เพลง", "Music")}
                      </TabsTrigger>
                      <TabsTrigger value="motion" className="gap-2">
                        <div className="flex items-center gap-2">
                          <FastForward className="h-4 w-4" />
                          <span>Motion</span>
                        </div>
                      </TabsTrigger>
                    </TabsList>
                  </Tabs>
                  {!user && (
                    <div className="mt-4 p-4 bg-primary/5 border border-primary/20 rounded-xl flex items-center justify-between gap-4">
                      <div className="flex items-center gap-2 text-primary">
                        <Sparkles className="h-5 w-5" />
                        <span className="text-sm font-medium">{t("เข้าสู่ระบบเพื่อเริ่มสร้างสรรค์!", "Login to start creating!")}</span>
                      </div>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => navigate("/login")}>{t("เข้าสู่ระบบ", "Login")}</Button>
                        <Button variant="hero" size="sm" onClick={() => navigate("/register")}>{t("สมัครสมาชิก", "Register")}</Button>
                      </div>
                    </div>
                  )}
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Prompt Input - Shared for Photo/Video/Music only */}
                  {activeTab !== "motion" && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <label className="text-sm font-medium">{t("คำอธิบาย", "Prompt")}</label>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="gap-2 text-muted-foreground hover:text-destructive"
                            onClick={() => {
                              setPromptText("");
                              setLyricsText("");
                            }}
                            disabled={!promptText && !lyricsText}
                          >
                            <Eraser className="h-4 w-4" />
                            {t("ล้าง", "Clear")}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="gap-2 text-primary"
                            onClick={handlePromptSuggestion}
                            disabled={!user}
                          >
                            <Wand2 className="h-4 w-4" />
                            {t("AI แนะนำ", "AI Suggest")}
                          </Button>
                        </div>
                      </div>
                      <Textarea
                        placeholder={t("อธิบายสิ่งที่คุณต้องการสร้าง...", "Describe what you want to generate...")}
                        value={promptText}
                        onChange={(e) => setPromptText(e.target.value)}
                        className="min-h-[120px] resize-none"
                        disabled={isGenerating && !allowNewGeneration}
                        maxLength={activeTab === "music" ? 500 : undefined}
                      />
                      {activeTab === "music" && (
                        <div className="flex justify-end">
                          <span className={`text-xs ${promptText.length >= 500 ? "text-destructive font-bold" : "text-muted-foreground"}`}>
                            {promptText.length}/500
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Photo Tab Content */}
                  {activeTab === "photo" && (
                    <div className="space-y-4">
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <label className="text-sm font-medium">{t("โมเดล", "Model")}</label>
                          <Button variant="outline" size="sm" className="gap-2 text-primary border-primary/20 hover:bg-primary/10" asChild>
                            <Link to="/generate/photobulk">
                              <Plus className="h-4 w-4" />
                              {t("สร้างรูปจำนวนมาก (Bulk)", "Bulk Photo Generation")}
                            </Link>
                          </Button>
                        </div>
                        <div className="grid grid-cols-4 gap-2">
                          {photoModels.map((model) => (
                            <button
                              key={model.id}
                              onClick={() => setSelectedPhotoModel(model.id)}
                              disabled={(isGenerating && !allowNewGeneration) || !user}
                              className={`p-2 rounded-lg border text-left transition-all ${selectedPhotoModel === model.id
                                ? "border-primary bg-primary/10"
                                : "border-border hover:border-primary/50"
                                } ${(isGenerating && !allowNewGeneration) || !user ? "opacity-50 cursor-not-allowed" : ""}`}
                            >
                              <p className="font-medium text-xs">{model.name}</p>
                              <p className="text-[10px] text-muted-foreground">{model.credits} {t("เครดิต", "credits")}</p>
                            </button>
                          ))}
                        </div>
                      </div>

                      {selectedPhotoModel !== "gpt-image" && (
                        <div>
                          <label className="text-sm font-medium mb-2 block">{t("สัดส่วนภาพ", "Aspect Ratio")}</label>
                          {isNanoBananaModel ? (
                            <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                              {nanoBananaAspectRatios.map((ratio) => (
                                <button
                                  key={ratio.value}
                                  onClick={() => setSelectedRatio(ratio.value)}
                                  disabled={isGenerating && !allowNewGeneration}
                                  className={`p-2 rounded-lg border text-center transition-all text-sm ${selectedRatio === ratio.value
                                    ? "border-primary bg-primary/10 text-primary"
                                    : "border-border hover:border-primary/50 text-muted-foreground"
                                    } ${isGenerating && !allowNewGeneration ? "opacity-50 cursor-not-allowed" : ""}`}
                                >
                                  {ratio.label}
                                </button>
                              ))}
                            </div>
                          ) : isMarketModel ? (
                            <div className="grid grid-cols-4 sm:grid-cols-8 gap-2">
                              {["1:1", "4:3", "3:4", "16:9", "9:16", "2:3", "3:2", "21:9"].map((r) => (
                                <button
                                  key={r}
                                  onClick={() => setSelectedRatio(r)}
                                  disabled={isGenerating && !allowNewGeneration}
                                  className={`p-2 rounded-lg border text-center transition-all text-sm ${selectedRatio === r
                                    ? "border-primary bg-primary/10 text-primary"
                                    : "border-border hover:border-primary/50 text-muted-foreground"
                                    } ${isGenerating && !allowNewGeneration ? "opacity-50 cursor-not-allowed" : ""}`}
                                >
                                  {r}
                                </button>
                              ))}
                            </div>
                          ) : (
                            <div className="grid grid-cols-4 gap-2">
                              {["1:1", "9:16", "16:9", "4:3"].map((r) => (
                                <button
                                  key={r}
                                  onClick={() => setSelectedRatio(r)}
                                  disabled={isGenerating && !allowNewGeneration}
                                  className={`p-2 rounded-lg border text-center transition-all text-sm ${selectedRatio === r
                                    ? "border-primary bg-primary/10 text-primary"
                                    : "border-border hover:border-primary/50 text-muted-foreground"
                                    } ${isGenerating && !allowNewGeneration ? "opacity-50 cursor-not-allowed" : ""}`}
                                >
                                  {r}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                      {selectedPhotoModel === "gpt-image" && (
                        <div className="p-3 rounded-lg bg-muted/30 border border-border">
                          <p className="text-sm text-muted-foreground">
                            {t("ขนาดภาพ: 1024x1024 (ขนาดเดียวสำหรับโมเดลนี้)", "Image size: 1024x1024 (fixed for this model)")}
                          </p>
                        </div>
                      )}

                      {supportsImage && (
                        <div>
                          <label className="text-sm font-medium mb-2 block">
                            {selectedPhotoModel === "nanobana-pro"
                              ? t("ภาพอ้างอิง (สูงสุด 8 ภาพ)", "Reference Images (Up to 8)")
                              : t("ภาพอ้างอิง (ไม่บังคับ)", "Reference Image (Optional)")}
                          </label>

                          {/* Single image upload for non-Nano Banana Pro models */}
                          {selectedPhotoModel !== "nanobana-pro" && (
                            <>
                              <input
                                type="file"
                                id="ref-image-upload"
                                accept="image/*"
                                className="hidden"
                                onChange={handleImageUpload}
                                disabled={isGenerating && !allowNewGeneration}
                              />

                              {referenceImage || referenceImageUrl ? (
                                <div className="relative rounded-lg overflow-hidden border border-border group/image">
                                  <img
                                    src={referenceImage ? URL.createObjectURL(referenceImage) : (referenceImageUrl || "")}
                                    alt="Reference"
                                    className="w-full h-auto max-h-[350px] object-contain bg-black/10 dark:bg-white/5"
                                  />
                                  <div className="absolute top-2 right-2 flex gap-1">
                                    <Button
                                      size="icon"
                                      variant="destructive"
                                      className="h-8 w-8 rounded-full shadow-md hover:scale-110 transition-transform"
                                      onClick={() => {
                                        setReferenceImage(null);
                                        setReferenceImageUrl(null);
                                      }}
                                      disabled={isGenerating && !allowNewGeneration}
                                    >
                                      <X className="h-4 w-4" />
                                    </Button>
                                  </div>
                                </div>
                              ) : (
                                <label
                                  htmlFor="ref-image-upload"
                                  className={`block border-2 border-dashed border-border rounded-lg p-8 text-center hover:border-primary/50 transition-colors bg-muted/10 hover:bg-muted/30 ${isGenerating && !allowNewGeneration ? "cursor-not-allowed opacity-50" : "cursor-pointer"}`}
                                >
                                  <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                                  <p className="text-sm text-muted-foreground">
                                    {t("คลิกเพื่ออัพโหลดหรือลากไฟล์มาวาง", "Click to upload or drag and drop")}
                                  </p>
                                </label>
                              )}
                            </>
                          )}

                          {/* Multiple image upload for Nano Banana Pro */}
                          {selectedPhotoModel === "nanobana-pro" && (
                            <>
                              <input
                                type="file"
                                id="ref-images-upload"
                                accept="image/jpeg,image/png,image/webp"
                                multiple
                                className="hidden"
                                onChange={handleMultipleImageUpload}
                                disabled={isGenerating && !allowNewGeneration}
                              />

                              {/* Display uploaded images */}
                              {referenceImages.length > 0 && (
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
                                  {referenceImages.map((img, index) => (
                                    <div key={index} className="relative rounded-lg overflow-hidden border border-border group/image aspect-square">
                                      <img
                                        src={URL.createObjectURL(img)}
                                        alt={`Reference ${index + 1}`}
                                        className="w-full h-full object-cover"
                                      />
                                      <div className="absolute top-1 right-1">
                                        <Button
                                          size="icon"
                                          variant="destructive"
                                          className="h-6 w-6 rounded-full shadow-md hover:scale-110 transition-transform"
                                          onClick={() => removeReferenceImage(index)}
                                          disabled={isGenerating && !allowNewGeneration}
                                        >
                                          <X className="h-3 w-3" />
                                        </Button>
                                      </div>
                                      <div className="absolute bottom-1 left-1 bg-black/60 text-white text-xs px-1.5 py-0.5 rounded">
                                        {index + 1}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}

                              {/* Upload area */}
                              {referenceImages.length < 8 && (
                                <label
                                  htmlFor="ref-images-upload"
                                  className={`block border-2 border-dashed border-border rounded-lg p-6 text-center hover:border-primary/50 transition-colors bg-muted/10 hover:bg-muted/30 ${isGenerating && !allowNewGeneration ? "cursor-not-allowed opacity-50" : "cursor-pointer"}`}
                                >
                                  <Upload className="h-6 w-6 mx-auto mb-2 text-muted-foreground" />
                                  <p className="text-sm text-muted-foreground">
                                    {referenceImages.length > 0
                                      ? t(`เพิ่มภาพอีก (${referenceImages.length}/8)`, `Add more images (${referenceImages.length}/8)`)
                                      : t("คลิกเพื่ออัพโหลดภาพ (สูงสุด 8 ภาพ)", "Click to upload images (max 8)")}
                                  </p>
                                  <p className="text-xs text-muted-foreground mt-1">
                                    {t("รองรับ JPG, PNG, WEBP (ไม่เกิน 30MB ต่อภาพ)", "Supports JPG, PNG, WEBP (max 30MB each)")}
                                  </p>
                                </label>
                              )}

                              {referenceImages.length > 0 && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="mt-2 text-destructive hover:text-destructive"
                                  onClick={() => setReferenceImages([])}
                                  disabled={isGenerating && !allowNewGeneration}
                                >
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  {t("ล้างภาพทั้งหมด", "Clear all images")}
                                </Button>
                              )}
                            </>
                          )}

                          {/* Recent Uploads Gallery - only for non-Nano Banana Pro */}
                          {selectedPhotoModel !== "nanobana-pro" && (
                            <div className="mt-4">
                              <RecentUploads
                                onSelectImage={(url) => {
                                  if (isGenerating && !allowNewGeneration) return;
                                  setReferenceImageUrl(url);
                                  setReferenceImage(null);
                                }}
                              />
                            </div>
                          )}

                          {/* Show selected from gallery - only for non-Nano Banana Pro */}
                          {selectedPhotoModel !== "nanobana-pro" && referenceImageUrl && !referenceImage && (
                            <div className="mt-3 relative rounded-lg overflow-hidden border border-primary/50 group/image">
                              <img
                                src={referenceImageUrl}
                                alt="Selected from gallery"
                                className="w-full h-auto max-h-[350px] object-contain bg-black/10 dark:bg-white/5"
                              />
                              <div className="absolute top-2 right-2">
                                <Button
                                  size="icon"
                                  variant="destructive"
                                  className="h-8 w-8 rounded-full shadow-md hover:scale-110 transition-transform"
                                  onClick={() => setReferenceImageUrl(null)}
                                  disabled={isGenerating && !allowNewGeneration}
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              </div>
                              <div className="absolute bottom-2 left-2 bg-black/60 text-white text-xs px-2 py-1 rounded">
                                {t("จาก Gallery", "From Gallery")}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Video Tab Content */}
                  {activeTab === "video" && (
                    <div className="space-y-4">
                      <div>
                        <div className="flex flex-col sm:flex-row items-center justify-between mb-4 gap-4 p-4 rounded-2xl bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/20 shadow-sm">
                          <div>
                            <p className="font-bold text-lg flex items-center gap-2">
                              <Sparkles className="h-5 w-5 text-purple-500" />
                              {t("แนะนำ Grok Imagine!", "Try Grok Imagine!")}
                            </p>
                            <p className="text-xs text-muted-foreground">{t("สร้างวิดีโอและรูปภาพ AI คุณภาพสูงด้วย Grok จาก xAI (Sora2 จะปิดตัว 26 เม.ย. นี้)", "Create high-quality AI videos & images with Grok from xAI (Sora2 shutting down Apr 26)")}</p>
                          </div>
                          <Button variant="hero" size="sm" className="gap-2 rounded-full px-6 shadow-purple-500/20" asChild>
                            <Link to="/generate/grok">
                              {t("Grok คลิกที่นี่", "Grok Click Here")}
                              <ArrowRight className="h-4 w-4" />
                            </Link>
                          </Button>
                        </div>
                        <div className="flex items-center justify-between mb-2">
                          <label className="text-sm font-medium">{t("รุ่นโมเดล", "Model")}</label>
                          <Button variant="outline" size="sm" className="gap-2 text-primary border-primary/20 hover:bg-primary/10" asChild>
                            <Link to="/generate/videobulk">
                              <Plus className="h-4 w-4" />
                              {t("สร้างวิดีโอจำนวนมาก (Bulk)", "Bulk Video Generation")}
                            </Link>
                          </Button>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 text-left">
                          {videoModels.map((model) => (
                            <button
                              key={model.id}
                              onClick={() => setSelectedVideoModel(model.id)}
                              disabled={(isGenerating && !allowNewGeneration) || !user}
                              className={`p-3 rounded-lg border text-left transition-all ${selectedVideoModel === model.id
                                ? "border-primary bg-primary/10"
                                : "border-border hover:border-primary/50"
                                } ${(isGenerating && !allowNewGeneration) || !user ? "opacity-50 cursor-not-allowed" : ""}`}
                            >
                              <p className="font-medium text-sm">{model.name}</p>
                              <p className="text-xs text-muted-foreground">{model.credits} {t("เครดิต", "credits")} • {model.duration}</p>
                            </button>
                          ))}
                        </div>
                      </div>

                      {user ? (
                        <>
                          <div>
                            <label className="text-sm font-medium mb-2 block">{t("สัดส่วน", "Aspect Ratio")}</label>
                            <div className="grid grid-cols-2 gap-2">
                              {videoAspectOptions.map((option) => (
                                <button
                                  key={option.value}
                                  onClick={() => setSelectedRatio(option.value)}
                                  disabled={isGenerating && !allowNewGeneration}
                                  className={`p-2 rounded-lg border text-center transition-all text-sm ${selectedRatio === option.value
                                    ? "border-primary bg-primary/10 text-primary"
                                    : "border-border hover:border-primary/50 text-muted-foreground"
                                    } ${isGenerating && !allowNewGeneration ? "opacity-50 cursor-not-allowed" : ""}`}
                                >
                                  {option.label}
                                </button>
                              ))}
                            </div>
                          </div>

                          {/* Resolution selector for Wan models */}
                          {selectedVideoModel.startsWith("wan26") && (() => {
                            const model = videoModels.find(m => m.id === selectedVideoModel);
                            const price720 = model?.credits || 0;
                            const price1080 = (model as any)?.credits1080 || 0;
                            return (
                              <div>
                                <label className="text-sm font-medium mb-2 block">{t("ความละเอียด", "Resolution")}</label>
                                <div className="grid grid-cols-2 gap-2">
                                  <button
                                    onClick={() => setWanResolution("720p")}
                                    disabled={isGenerating && !allowNewGeneration}
                                    className={`p-2 rounded-lg border text-center transition-all text-sm ${wanResolution === "720p"
                                      ? "border-primary bg-primary/10 text-primary"
                                      : "border-border hover:border-primary/50 text-muted-foreground"
                                      } ${isGenerating && !allowNewGeneration ? "opacity-50 cursor-not-allowed" : ""}`}
                                  >
                                    720p <span className="text-xs opacity-70">({price720})</span>
                                  </button>
                                  <button
                                    onClick={() => setWanResolution("1080p")}
                                    disabled={isGenerating && !allowNewGeneration}
                                    className={`p-2 rounded-lg border text-center transition-all text-sm ${wanResolution === "1080p"
                                      ? "border-primary bg-primary/10 text-primary"
                                      : "border-border hover:border-primary/50 text-muted-foreground"
                                      } ${isGenerating && !allowNewGeneration ? "opacity-50 cursor-not-allowed" : ""}`}
                                  >
                                    1080p <span className="text-xs opacity-70">({price1080})</span>
                                  </button>
                                </div>
                              </div>
                            );
                          })()}

                          {/* Full HD checkbox for Veo models */}
                          {selectedVideoModel.startsWith("veo31") && (
                            <div>
                              <label
                                className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                                  veoFullHD
                                    ? "border-primary bg-primary/10"
                                    : "border-border hover:border-primary/50"
                                }`}
                                onClick={() => setVeoFullHD(!veoFullHD)}
                              >
                                <input
                                  type="checkbox"
                                  checked={veoFullHD}
                                  onChange={(e) => setVeoFullHD(e.target.checked)}
                                  className="h-4 w-4 rounded border-border accent-primary"
                                />
                                <div className="flex-1">
                                  <p className="text-sm font-medium">{t("Full HD 1080p", "Full HD 1080p")}</p>
                                  <p className="text-xs text-muted-foreground">{t("อัปสเกลเป็น 1080p (+5 เครดิต)", "Upscale to 1080p (+5 credits)")}</p>
                                </div>
                                <Badge variant="outline" className="text-xs">+5 Cr.</Badge>
                              </label>
                            </div>
                          )}

                          {videoModels.find(m => m.id === selectedVideoModel)?.supportsImage && !isSoraVideo && (
                            <div>
                              <label className="text-sm font-medium mb-2 block">
                                {t("เฟรมเริ่มต้น (ไม่บังคับ)", "Starting Frame (Optional)")}
                              </label>

                              <input
                                type="file"
                                id="video-ref-image-upload"
                                accept="image/*"
                                className="hidden"
                                onChange={handleImageUpload}
                                disabled={isGenerating && !allowNewGeneration}
                              />

                              {referenceImage || referenceImageUrl ? (
                                <div className="relative rounded-lg overflow-hidden border border-border group/image">
                                  <img
                                    src={referenceImage ? URL.createObjectURL(referenceImage) : (referenceImageUrl || "")}
                                    alt="Starting Frame"
                                    className="w-full h-auto max-h-[350px] object-contain bg-black/10 dark:bg-white/5"
                                  />
                                  <div className="absolute top-2 right-2">
                                    <Button
                                      size="icon"
                                      variant="destructive"
                                      className="h-8 w-8 rounded-full shadow-md hover:scale-110 transition-transform"
                                      onClick={() => {
                                        setReferenceImage(null);
                                        setReferenceImageUrl(null);
                                      }}
                                      disabled={isGenerating && !allowNewGeneration}
                                    >
                                      <X className="h-4 w-4" />
                                    </Button>
                                  </div>
                                </div>
                              ) : (
                                <label
                                  htmlFor="video-ref-image-upload"
                                  className={`block border-2 border-dashed border-border rounded-lg p-8 text-center hover:border-primary/50 transition-colors bg-muted/10 hover:bg-muted/30 ${isGenerating && !allowNewGeneration ? "cursor-not-allowed opacity-50" : "cursor-pointer"}`}
                                >
                                  <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                                  <p className="text-sm text-muted-foreground">
                                    {t("อัพโหลดภาพเพื่อใช้เป็นเฟรมเริ่มต้น", "Upload an image to use as starting frame")}
                                  </p>
                                </label>
                              )}
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="relative min-h-[250px] rounded-2xl overflow-hidden border border-white/20 bg-white/5 backdrop-blur-sm mt-4">
                          <GuestOverlay
                            title={t("เข้าสู่ระบบเพื่อปลดล็อควิดีโอ", "Login to Unlock Video Options")}
                            description={t("สมัครสมาชิกเพื่อเข้าถึงการตั้งค่าระดับสูงและเริ่มสร้างวิดีโอคุณภาพสูง", "Sign up to access advanced settings and start creating high-quality videos.")}
                            showIcon={true}
                          />
                        </div>
                      )}
                    </div>
                  )}

                  {/* Music Tab Content */}
                  {activeTab === "music" && (
                    <div className="space-y-4">
                      <Card className="bg-muted/30 border-border">
                        <CardContent className="p-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-magenta-500/20 flex items-center justify-center">
                              <Music className="h-5 w-5 text-magenta-500" />
                            </div>
                            <div>
                              <p className="font-medium">Suno Music V6</p>
                              <p className="text-sm text-muted-foreground">12 {t("เครดิตต่อการสร้าง", "credits per generation")}</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                      <p className="text-sm text-muted-foreground">
                        {t("อธิบายแนวเพลง อารมณ์ เครื่องดนตรี และสไตล์ที่คุณต้องการสร้าง", "Describe the genre, mood, instruments, and style of music you want to create.")}
                      </p>

                      {/* Lyrics Textarea - shows when NOT instrumental */}
                      {!isInstrumental && (
                        <div className="space-y-2">
                          <label className="text-sm font-medium">{t("เนื้อเพลง (ไม่บังคับ)", "Lyrics (Optional)")}</label>
                          <Textarea
                            placeholder={t("พิมพ์เนื้อเพลงที่ต้องการให้ร้อง... (ถ้าไม่ใส่ ระบบจะสร้างเนื้อเพลงให้อัตโนมัติ)", "Type lyrics you want to be sung... (if empty, lyrics will be auto-generated)")}
                            value={lyricsText}
                            onChange={(e) => setLyricsText(e.target.value)}
                            className="min-h-[100px] resize-none"
                            disabled={isGenerating}
                            maxLength={5000}
                          />
                          <div className="flex justify-end">
                            <span className={`text-xs ${lyricsText.length >= 5000 ? "text-destructive font-bold" : "text-muted-foreground"}`}>
                              {lyricsText.length}/5000
                            </span>
                          </div>
                        </div>
                      )}

                      <div className="flex items-center justify-between p-4 rounded-xl border border-border bg-muted/20">
                        <div className="space-y-0.5">
                          <label className="text-sm font-medium">{t("ไม่มีเสียงร้อง (Instrumental)", "Instrumental (No Vocals)")}</label>
                          <p className="text-xs text-muted-foreground">{t("สร้างเฉพาะทำนองเพลงเท่านั้น", "Generate only melody without singing")}</p>
                        </div>
                        <button
                          onClick={() => setIsInstrumental(!isInstrumental)}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${isInstrumental ? "bg-primary" : "bg-muted-foreground/30"
                            }`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${isInstrumental ? "translate-x-6" : "translate-x-1"
                              }`}
                          />
                        </button>
                      </div>

                      {/* 14-day retention warning */}
                      <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 text-amber-700 dark:text-amber-400">
                        <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                        <p className="text-xs">
                          {t("ไฟล์เพลงเก็บไว้ให้แค่ 14 วัน กรุณากดโหลดออกด้วยนะครับ", "Music files are only kept for 14 days. Please download them!")}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Motion Tab Content (Video Dance) */}
                  {activeTab === "motion" && (
                    <VideoDanceTab userCredits={userCredits || 0} />
                  )}

                  {/* Credit Warning */}
                  {!hasEnoughCredits && activeTab !== "motion" && (
                    <div className="flex items-start gap-3 p-4 rounded-lg bg-destructive/10 border border-destructive/20">
                      <AlertTriangle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
                      <div className="space-y-1">
                        <p className="text-sm font-medium text-destructive">
                          {t("เครดิตไม่เพียงพอ", "Insufficient Credits")}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {t(
                            `คุณต้องการ ${getRequiredCredits()} เครดิต แต่คุณมีเพียง ${(userCredits || 0).toLocaleString()} เครดิต`,
                            `You need ${getRequiredCredits()} credits but only have ${(userCredits || 0).toLocaleString()} credits`
                          )}
                        </p>
                        <Link to="/pricing">
                          <Button variant="outline" size="sm" className="mt-2 text-destructive border-destructive/30 hover:bg-destructive/10">
                            <Coins className="h-4 w-4 mr-2" />
                            {t("ซื้อเครดิต", "Buy Credits")}
                          </Button>
                        </Link>
                      </div>
                    </div>
                  )}

                  {/* Generate Button */}
                  {activeTab !== "motion" && (
                    <div className={`space-y-2 relative ${!user && activeTab === 'video' ? 'hidden' : ''}`}>
                      {!user && (
                        <GuestOverlay
                          title={t("ปลดล็อคจินตนาการของคุณ", "Unlock Your Imagination")}
                          description={t("สมัครสมาชิกเพื่อเริ่มสร้างภาพ วิดีโอ หรือเพลงด้วยพลัง AI ได้ทันที", "Register to start creating AI photos, videos, or music instantly.")}
                          showIcon={false}
                        />
                      )}
                      <div className={!user ? "opacity-20 blur-[1px] pointer-events-none" : ""}>
                        <div className="space-y-2">
                          <Button
                            variant="hero"
                            size="lg"
                            className="w-full gap-2"
                            onClick={handleGenerate}
                            disabled={(isGenerating && !allowNewGeneration) || !promptText.trim() || !hasEnoughCredits || !user}
                          >
                            {isGenerating && !allowNewGeneration ? (
                              <>
                                <Loader2 className="h-5 w-5 animate-spin" />
                                {t("กำลังสร้าง...", "Generating...")}
                              </>
                            ) : (
                              <>
                                <Sparkles className="h-5 w-5" />
                                {t("สร้าง", "Generate")} ({getRequiredCredits()} {t("เครดิต", "credits")})
                              </>
                            )}
                          </Button>

                          {/* Continue without waiting button - shows during generation */}
                          {isGenerating && showContinueButton && !allowNewGeneration && (
                            <Button
                              variant="outline"
                              size="lg"
                              className="w-full gap-2 border-primary/30 text-primary hover:bg-primary/10"
                              onClick={handleContinueWithoutWaiting}
                            >
                              <FastForward className="h-5 w-5" />
                              {t("สร้างต่อเลย ไม่รอ", "Continue, don't wait")}
                            </Button>
                          )}

                          {activeTab === "video" && !selectedVideoModel.startsWith("veo31") && (
                            <Button
                              variant="outline"
                              size="lg"
                              className="w-full gap-2 border-amber-500/30 text-amber-600 hover:bg-amber-500/10"
                              onClick={handleMagicGenerate}
                              disabled={!hasEnoughMagicCredits || magicLoading || (isGenerating && !allowNewGeneration) || !user}
                            >
                              {magicLoading ? (
                                <Loader2 className="h-5 w-5 animate-spin" />
                              ) : (
                                <Sparkles className="h-5 w-5" />
                              )}
                              {t("คิดไม่ออก ให้ AI สุ่มคลิปไวรัลให้", "Need ideas? AI will random a viral clip")}
                              <span className="text-xs opacity-80">+{MAGIC_EXTRA_CREDITS} {t("เครดิต", "credits")}</span>
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Result Panel */}
            <div className="hidden lg:block lg:col-span-1">
              <Card variant="glass" className="sticky top-24">
                <CardHeader>
                  <CardTitle>{t("ผลลัพธ์", "Result")}</CardTitle>
                  <CardDescription>{t("ผลลัพธ์ที่สร้างจะแสดงที่นี่", "Your generated content will appear here")}</CardDescription>
                </CardHeader>
                <CardContent>
                  {isGenerating && (
                    <div className="mb-6 space-y-4 animate-in fade-in slide-in-from-top-4 duration-500">
                      <div className="flex items-center gap-4 p-4 rounded-2xl border border-primary/20 bg-primary/5 backdrop-blur-sm relative overflow-hidden group">
                        <div className="absolute top-0 right-0 w-24 h-24 -mr-8 -mt-8 bg-primary/10 rounded-full blur-2xl group-hover:bg-primary/20 transition-all duration-500"></div>

                        <div className="relative">
                          <div className="absolute inset-0 bg-primary/20 rounded-full blur-md animate-pulse"></div>
                          <div className="bg-background/80 backdrop-blur-sm rounded-full p-2 border border-primary/20 relative shadow-sm">
                            <Loader2 className="h-5 w-5 text-primary animate-spin" />
                          </div>
                        </div>

                        <div className="flex-1 text-left">
                          <p className="text-sm font-bold text-foreground mb-0.5">{t("กำลังสร้าง...", "Generating content...")}</p>
                          <div className="flex items-center gap-1.5">
                            <div className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse"></div>
                            <p className="text-[11px] text-muted-foreground font-medium">{statusMessage}</p>
                          </div>
                        </div>

                        <div className="text-right">
                          <span className="text-sm font-mono font-bold text-primary">{Math.round(generationProgress)}%</span>
                        </div>
                      </div>

                      <div className="px-1">
                        <Progress value={generationProgress} className="h-1.5 bg-primary/10" />
                      </div>
                    </div>
                  )}
                  {generatedResult ? (
                    <div className="space-y-4 text-left">
                      <div className="rounded-lg bg-muted/50 overflow-hidden">
                        {activeTab === "photo" && (
                          <img
                            src={generatedResult}
                            alt="Generated content"
                            className="w-full h-auto object-contain"
                          />
                        )}
                        {activeTab === "video" && (
                          <video
                            src={generatedResult}
                            controls
                            className="w-full h-auto"
                          />
                        )}
                        {activeTab === "music" && (() => {
                          // Try to parse as JSON (new format with cover images)
                          let tracks: { audio: string; image?: string; title?: string; duration?: number }[] = [];
                          try {
                            const parsed = JSON.parse(generatedResult);
                            if (parsed.tracks) tracks = parsed.tracks;
                          } catch {
                            // Fallback to comma-separated URLs
                            tracks = generatedResult.split(',').filter(u => u.startsWith('http')).map(u => ({ audio: u }));
                          }

                          return (
                            <div className="p-4 space-y-4">
                              {tracks.map((track, idx) => (
                                <div key={idx} className="space-y-3 p-3 rounded-lg bg-muted/30">
                                  <div className="flex items-center gap-3">
                                    {track.image && (
                                      <img
                                        src={track.image}
                                        alt={track.title || `Track ${idx + 1}`}
                                        className="w-16 h-16 rounded-lg object-cover shadow-md"
                                      />
                                    )}
                                    <div className="flex-1">
                                      <p className="text-sm font-semibold">{track.title || `Track ${idx + 1}`}</p>
                                      {track.duration && (
                                        <p className="text-xs text-muted-foreground">
                                          {Math.floor(track.duration / 60)}:{String(Math.floor(track.duration % 60)).padStart(2, '0')}
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                  <audio src={getMusicAudioUrl(track.audio)} controls className="w-full" />
                                </div>
                              ))}
                            </div>
                          );
                        })()}
                      </div>

                      <div className="flex flex-wrap gap-2">
                        {(() => {
                          let tracks: { audio: string; title?: string }[] = [];
                          try {
                            const parsed = JSON.parse(generatedResult);
                            if (parsed.tracks) tracks = parsed.tracks;
                          } catch {
                            tracks = generatedResult.split(',').filter(u => u.startsWith('http')).map(u => ({ audio: u }));
                          }

                          return tracks.map((track, idx) => (
                            <Button
                              key={idx}
                              variant="outline"
                              size="sm"
                              className="flex-1 min-w-[120px] gap-2"
                              onClick={() => handleDownload(track.audio, `${track.title || `song_${idx + 1}`}.mp3`)}
                            >
                              <Download className="h-4 w-4" />
                              {tracks.length > 1 ? `Song ${idx + 1}` : t("ดาวน์โหลด", "Download")}
                            </Button>
                          ));
                        })()}
                      </div>

                      {/* Generation Info */}
                      {lastGeneration && (
                        <div className="flex items-center gap-2 p-3 rounded-lg bg-green-500/10 border border-green-500/20">
                          <CheckCircle2 className="h-4 w-4 text-green-500" />
                          <div className="text-sm">
                            <p className="font-medium text-green-600">
                              {t("สร้างสำเร็จ!", "Generated successfully!")}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {lastGeneration.model} • -{lastGeneration.credits} {t("เครดิต", "credits")}
                            </p>
                          </div>
                        </div>
                      )}

                      <Button variant="glow" className="w-full gap-2">
                        <Download className="h-4 w-4" />
                        {t("ดาวน์โหลด", "Download")}
                      </Button>
                    </div>
                  ) : (
                    <div className="aspect-square rounded-lg bg-muted/30 border-2 border-dashed border-border flex items-center justify-center">
                      <div className="text-center">
                        <Sparkles className="h-12 w-12 mx-auto mb-3 text-muted-foreground/50" />
                        <p className="text-sm text-muted-foreground">{t("ยังไม่ได้สร้าง", "No generation yet")}</p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>

        {/* Recent Motion Section - Only show on Motion tab */}
        {
          activeTab === "motion" && recentMotion.length > 0 && (
            <div className="mt-12" id="recent-history-section">
              <Card className="rounded-3xl border border-white/20 shadow-xl bg-white/50 backdrop-blur-md dark:bg-black/40 p-6 md:p-8">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-xl font-heading font-bold">{t("วิดีโอ Motion ล่าสุด", "Recent Motion Videos")}</h2>
                    <p className="text-sm text-muted-foreground">{t("วิดีโอ Motion ที่คุณสร้างล่าสุด", "Your recently generated motion videos")}</p>
                  </div>
                  <Link to="/history/motion">
                    <Button variant="outline" className="gap-2 bg-white/50 border-white/20 hover:bg-white/80">
                      <History className="h-4 w-4" />
                      {t("ดูทั้งหมด", "View All")}
                    </Button>
                  </Link>
                </div>

                {loadingMotion ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                    {recentMotion.map((video) => {
                      let videoUrl = video.result_url || "";
                      if (videoUrl.startsWith("{")) {
                        try {
                          const parsed = JSON.parse(videoUrl);
                          videoUrl = parsed.resultUrls?.[0] || parsed.url || "";
                        } catch { }
                      } else if (videoUrl.includes(",")) {
                        videoUrl = videoUrl.split(",")[0].trim();
                      }

                      const isCompleted = video.status === "completed" && videoUrl && videoUrl.startsWith("http");
                      const isFailed = video.status === "failed";
                      const canCheckStatus = !isCompleted && !isFailed;

                      return (
                        <Card
                          key={video.id}
                          className="group overflow-hidden border border-white/20 shadow-xl bg-white/80 backdrop-blur-md dark:bg-black/60 hover:bg-white transition-all rounded-3xl"
                        >
                          <div className="p-3">
                            <div className="relative w-full rounded-2xl overflow-hidden shadow-md border border-black/5 bg-black/5">
                              {isCompleted && videoUrl ? (
                                <video
                                  src={videoUrl}
                                  className="w-full h-auto max-h-[600px] block bg-black/10"
                                  controls
                                  playsInline
                                  preload="metadata"
                                />
                              ) : (
                                <div className="w-full aspect-video flex flex-col items-center justify-center bg-gradient-to-br from-primary/10 to-purple-500/10 gap-3">
                                  {isFailed ? (
                                    <>
                                      <AlertTriangle className="h-10 w-10 text-destructive" />
                                      <p className="text-xs text-destructive font-medium">{t("ผิดพลาด", "Failed")}</p>
                                      {video.error_message && (
                                        <p className="text-[10px] text-destructive/70 text-center px-4 line-clamp-3">{video.error_message}</p>
                                      )}
                                    </>
                                  ) : (
                                    <>
                                      <Clock className="h-10 w-10 text-muted-foreground animate-pulse" />
                                      <p className="text-xs text-muted-foreground">{t("กำลังสร้าง...", "Generating...")}</p>
                                      {canCheckStatus && (
                                        <Button
                                          variant="glow"
                                          size="sm"
                                          onClick={() => handleCheckStatus(video.id, "motion")}
                                          disabled={checkingStatus === video.id}
                                          className="mt-2"
                                        >
                                          {checkingStatus === video.id ? (
                                            <Loader2 className="h-4 w-4 animate-spin" />
                                          ) : (
                                            <>
                                              <RefreshCw className="h-4 w-4 mr-2" />
                                              {t("เช็กสถานะ", "Check Status")}
                                            </>
                                          )}
                                        </Button>
                                      )}
                                    </>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>

                          <CardContent className="p-5 pt-0 mt-auto">
                            <p className="text-sm font-medium line-clamp-1 text-foreground/90">{getDisplayPrompt(video)}</p>
                            <div className="flex items-center justify-between mt-2">
                              <p className="text-[10px] text-primary/70 font-semibold bg-primary/10 px-1.5 py-0.5 rounded uppercase">
                                Motion
                              </p>
                              <div className="flex items-center gap-2">
                                {getStatusBadge(video.status)}
                              </div>
                            </div>

                            <div className="flex items-center justify-between mt-4">
                              <div className="flex flex-col gap-1">
                                <div className="flex items-center gap-3">
                                  <div>
                                    <span className="text-[10px] text-muted-foreground font-bold uppercase tracking-tighter">{t("เมื่อวันที่", "Date")}</span>
                                    <span className="text-xs font-semibold block">{formatDateHistory(video.created_at)}</span>
                                  </div>
                                  {video.credits_used !== undefined && (
                                    <div className="border-l border-white/10 pl-3">
                                      <span className="text-[10px] text-muted-foreground font-bold uppercase tracking-tighter">{t("เครดิต", "Credits")}</span>
                                      {video.status === "failed" ? (
                                        <span className="text-xs font-semibold text-green-500 block">0 ({t("คืนแล้ว", "Refunded")})</span>
                                      ) : (
                                        <span className="text-xs font-semibold text-primary block">-{video.credits_used}</span>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-1">
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="h-8 w-8 text-muted-foreground hover:text-primary rounded-lg transition-colors"
                                  onClick={() => handleReusePrompt(getDisplayPrompt(video))}
                                >
                                  <Repeat className="h-4 w-4" />
                                </Button>
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="h-8 w-8 text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded-lg transition-colors"
                                  onClick={() => handleDeleteHistory(video.id, "video")}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                                {isCompleted && videoUrl && (
                                  <div className="flex gap-1">
                                    <Button
                                      size="icon"
                                      variant="ghost"
                                      className="h-8 w-8 text-primary hover:bg-primary/10 rounded-lg transition-colors relative"
                                      onClick={() => handleDownload(videoUrl, `motion-video-${video.id.slice(0, 8)}.mp4`)}
                                    >
                                      <Download className="h-4 w-4" />
                                    </Button>
                                  </div>
                                )}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </Card>
            </div>
          )
        }

        {/* Recent Photos Section - Only show on Photo tab */}
        {
          activeTab === "photo" && recentPhotos.length > 0 && (
            <div className="mt-12" id="recent-history-section">
              <Card className="rounded-3xl border border-white/20 shadow-xl bg-white/50 backdrop-blur-md dark:bg-black/40 p-6 md:p-8">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-xl font-heading font-bold">{t("รูปภาพล่าสุด", "Recent Photos")}</h2>
                    <p className="text-sm text-muted-foreground">{t("รูปภาพที่คุณสร้างล่าสุด", "Your recently generated photos")}</p>
                  </div>
                  <Link to="/history">
                    <Button variant="outline" className="gap-2 bg-white/50 border-white/20 hover:bg-white/80">
                      <History className="h-4 w-4" />
                      {t("ดูทั้งหมด", "View All")}
                    </Button>
                  </Link>
                </div>

                {loadingPhotos ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                    {recentPhotos.map((photo) => {
                      const rawResult = photo.result_url || "";
                      let previewUrl = rawResult;
                      if (previewUrl.startsWith("{")) {
                        try {
                          const parsed = JSON.parse(previewUrl);
                          previewUrl = parsed.resultUrls?.[0] || parsed.url || previewUrl;
                        } catch { }
                      } else if (previewUrl.includes(",")) {
                        previewUrl = previewUrl.split(",")[0].trim();
                      }

                      const hasImagePreview = previewUrl.startsWith("http") || previewUrl.startsWith("data:");
                      const modelValue = photo.model?.toLowerCase() || "";
                      const promptValue = photo.prompt?.toLowerCase() || "";
                      const isImageAnalysis = rawResult === "vision-response" ||
                        modelValue.includes("vision") ||
                        modelValue.includes("analyze") ||
                        promptValue.includes("image analysis");
                      const reuseDisabled = photo.is_magic || isImageAnalysis;

                      return (
                        <Card
                          key={photo.id}
                          className="group overflow-hidden border border-white/20 shadow-xl bg-white/80 backdrop-blur-md dark:bg-black/60 hover:bg-white transition-all rounded-3xl"
                        >
                          <div className="p-3">
                            <div
                              className={`relative w-full aspect-square rounded-2xl overflow-hidden shadow-md border border-black/5 bg-black/5 group-hover:scale-[1.02] transition-transform duration-500 ${hasImagePreview ? "cursor-pointer" : ""}`}
                              onClick={() => {
                                if (hasImagePreview) {
                                  setSelectedImage(previewUrl);
                                }
                              }}
                            >
                              {hasImagePreview ? (
                                <img
                                  src={previewUrl}
                                  alt={getDisplayPrompt(photo)}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                  <div className="text-center">
                                    <Image className="h-10 w-10 mx-auto mb-2 text-muted-foreground/60" />
                                    <p className="text-[10px] text-muted-foreground">
                                      {isImageAnalysis ? t("ผลลัพธ์อยู่ใน AI Chat", "Result available in AI Chat") : t("ไม่มีพรีวิว", "No preview")}
                                    </p>
                                  </div>
                                </div>
                              )}
                              {/* Action Buttons Overlay removed - moved to footer */}
                            </div>
                          </div>
                          <CardContent className="p-5 pt-0 mt-auto">
                            <p className="text-sm font-medium line-clamp-1 text-foreground/90">{getDisplayPrompt(photo)}</p>
                            <div className="flex items-center justify-between mt-2">
                              <p className="text-[10px] text-primary/70 font-semibold bg-primary/10 px-1.5 py-0.5 rounded uppercase">
                                {photo.model}
                              </p>
                              <div className="flex items-center gap-2">
                                {getStatusBadge(photo.status)}
                              </div>
                            </div>

                            <div className="flex items-center justify-between mt-4">
                              <div className="flex flex-col gap-1">
                                <div className="flex items-center gap-3">
                                  <div>
                                    <span className="text-[10px] text-muted-foreground font-bold uppercase tracking-tighter">{t("เมื่อวันที่", "Date")}</span>
                                    <span className="text-xs font-semibold block">{formatDateHistory(photo.created_at)}</span>
                                  </div>
                                  {photo.credits_used !== undefined && (
                                    <div className="border-l border-white/10 pl-3">
                                      <span className="text-[10px] text-muted-foreground font-bold uppercase tracking-tighter">{t("เครดิต", "Credits")}</span>
                                      {photo.status === "failed" ? (
                                        <span className="text-xs font-semibold text-green-500 block">0 ({t("คืนแล้ว", "Refunded")})</span>
                                      ) : (
                                        <span className="text-xs font-semibold text-primary block">-{photo.credits_used}</span>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-1">
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="h-8 w-8 text-muted-foreground hover:text-primary rounded-lg transition-colors"
                                  title={reuseDisabled ? t("ไม่รองรับการใช้ซ้ำ", "Reuse not available") : t("ใช้พรอมต์ซ้ำ", "Reuse Prompt")}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (reuseDisabled) return;
                                    handleReusePrompt(getDisplayPrompt(photo));
                                  }}
                                  disabled={reuseDisabled}
                                >
                                  <Repeat className="h-4 w-4" />
                                </Button>
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="h-8 w-8 text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded-lg transition-colors"
                                  onClick={(e) => { e.stopPropagation(); handleDeleteHistory(photo.id, "photo"); }}
                                  title={t("ลบ", "Delete")}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                                {photo.status === 'completed' && hasImagePreview && (
                                  <>
                                    <Button
                                      size="icon"
                                      variant="ghost"
                                      className="h-8 w-8 text-purple-600 hover:bg-purple-500/10 rounded-lg transition-colors"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleAnimatePhoto(previewUrl, photo.prompt);
                                      }}
                                      title={t("สร้างวิดีโอจากภาพนี้ (Veo)", "Animate this (Veo)")}
                                    >
                                      <Video className="h-4 w-4" />
                                    </Button>
                                    <Button
                                      size="icon"
                                      variant="ghost"
                                      className="h-8 w-8 text-amber-500 hover:bg-amber-500/10 rounded-lg transition-colors"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setEditingImageUrl(previewUrl);
                                        setEditPrompt("");
                                      }}
                                      title={t("แก้ไขด้วย AI", "Edit with AI")}
                                    >
                                      <Pencil className="h-4 w-4" />
                                    </Button>
                                    <Button
                                      size="icon"
                                      variant="ghost"
                                      className="h-8 w-8 text-primary hover:bg-primary/10 rounded-lg transition-colors"
                                      onClick={(e) => { e.stopPropagation(); handleDownload(previewUrl, `generated-photo-${photo.id.slice(0, 8)}.png`); }}
                                      title={t("ดาวน์โหลด", "Download")}
                                    >
                                      <Download className="h-4 w-4" />
                                    </Button>
                                  </>
                                )}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </Card>
            </div>
          )
        }

        {/* Recent Music Section - Only show on Music tab */}
        {
          activeTab === "music" && recentMusic.length > 0 && (
            <div className="mt-12" id="recent-history-section">
              <Card className="rounded-3xl border border-white/20 shadow-xl bg-white/50 backdrop-blur-md dark:bg-black/40 p-6 md:p-8">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-xl font-heading font-bold">{t("เพลงล่าสุด", "Recent Music")}</h2>
                    <p className="text-sm text-muted-foreground">{t("เพลงที่คุณสร้างล่าสุด", "Your recently generated music")}</p>
                  </div>
                  <Link to="/history/music">
                    <Button variant="outline" className="gap-2 bg-white/50 border-white/20 hover:bg-white/80">
                      <History className="h-4 w-4" />
                      {t("ดูทั้งหมด", "View All")}
                    </Button>
                  </Link>
                </div>

                {loadingMusic ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  </div>
                ) : (
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {recentMusic.map((music) => {
                      let coverImage = "";
                      let tracks: { audio: string; image?: string; title?: string }[] = [];
                      try {
                        const parsed = JSON.parse(music.result_url || "");
                        if (parsed.tracks && parsed.tracks.length > 0) {
                          tracks = parsed.tracks;
                          coverImage = parsed.tracks[0].image || "";
                        }
                      } catch {
                        // Fallback to comma-separated URLs  
                        tracks = (music.result_url || "").split(',')
                          .filter(u => u.startsWith('http'))
                          .map(u => ({ audio: u }));
                        coverImage = "";
                      }

                      return (
                        <Card
                          key={music.id}
                          variant="glass"
                          className="group hover:border-primary/30 transition-all overflow-hidden"
                        >
                          {/* Cover Image */}
                          <div className="relative aspect-square bg-gradient-to-br from-primary/20 to-secondary/20 overflow-hidden">
                            {coverImage ? (
                              <img
                                src={coverImage}
                                alt={getDisplayPrompt(music)}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <Music className="h-12 w-12 text-muted-foreground/50" />
                              </div>
                            )}
                            {/* Track count badge */}
                            {tracks.length > 1 && (
                              <div className="absolute top-2 right-2 bg-primary text-white text-xs px-2 py-1 rounded-full">
                                {tracks.length} {t("เพลง", "tracks")}
                              </div>
                            )}
                          </div>

                          <CardContent className="p-5 pt-0 mt-auto space-y-3">
                            <p className="text-sm font-medium line-clamp-2">{tracks[0]?.title || getDisplayPrompt(music).substring(0, 50)}</p>

                            {/* Audio Players */}
                            <div className="space-y-2">
                              {tracks.map((track, idx) => (
                                <div key={idx} className="space-y-1">
                                  {tracks.length > 1 && (
                                    <p className="text-xs text-muted-foreground">{track.title || `Track ${idx + 1}`}</p>
                                  )}
                                  <audio src={getMusicAudioUrl(track.audio)} controls className="w-full h-8" preload="metadata" />
                                </div>
                              ))}
                            </div>

                            <div className="flex items-center justify-between gap-1 pt-1">
                              <div className="flex flex-col gap-1">
                                <div className="flex items-center gap-3">
                                  <div>
                                    <span className="text-[10px] text-muted-foreground font-bold uppercase tracking-tighter">{t("เมื่อวันที่", "Date")}</span>
                                    <span className="text-xs font-semibold block">{formatDateHistory(music.created_at)}</span>
                                  </div>
                                  {music.credits_used !== undefined && (
                                    <div className="border-l border-white/10 pl-3">
                                      <span className="text-[10px] text-muted-foreground font-bold uppercase tracking-tighter">{t("เครดิต", "Credits")}</span>
                                      {music.status === "failed" ? (
                                        <span className="text-xs font-semibold text-green-500 block">0 ({t("คืนแล้ว", "Refunded")})</span>
                                      ) : (
                                        <span className="text-xs font-semibold text-primary block">-{music.credits_used}</span>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-1">
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="h-8 w-8 text-muted-foreground hover:text-primary rounded-lg transition-colors"
                                  title={music.is_magic ? t("พรอมต์ถูกซ่อน", "Prompt hidden") : t("ใช้พรอมต์ซ้ำ", "Reuse Prompt")}
                                  onClick={() => {
                                    if (music.is_magic) return;
                                    handleReusePrompt(getDisplayPrompt(music));
                                  }}
                                  disabled={music.is_magic}
                                >
                                  <Repeat className="h-4 w-4" />
                                </Button>
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="h-8 w-8 text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded-lg transition-colors"
                                  onClick={() => handleDeleteHistory(music.id, "music")}
                                  title={t("ลบ", "Delete")}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                                {music.status === 'completed' && tracks.length > 0 && (
                                  <div className="flex gap-1">
                                    {tracks.map((track, idx) => (
                                      <Button
                                        key={idx}
                                        variant="ghost"
                                        size="icon"
                                        className="h-8 w-8 text-primary hover:bg-primary/10 rounded-lg transition-colors relative"
                                        onClick={() => handleDownload(track.audio, `${track.title || `song_${idx + 1}`}.mp3`)}
                                        title={tracks.length > 1 ? `Download Track ${idx + 1}` : t("ดาวน์โหลด", "Download")}
                                      >
                                        <Download className="h-4 w-4" />
                                        {tracks.length > 1 && (
                                          <span className="absolute -top-1 -right-1 bg-primary text-[8px] text-white w-3 h-3 flex items-center justify-center rounded-full">
                                            {idx + 1}
                                          </span>
                                        )}
                                      </Button>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </Card>
            </div>
          )
        }

        {/* Recent Videos Section - Only show on Video tab */}
        {
          activeTab === "video" && recentVideos.length > 0 && (
            <div className="mt-12" id="recent-history-section">
              <Card className="rounded-3xl border border-white/20 shadow-xl bg-white/50 backdrop-blur-md dark:bg-black/40 p-6 md:p-8">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-xl font-heading font-bold">{t("วิดีโอล่าสุด", "Recent Videos")}</h2>
                    <p className="text-sm text-muted-foreground">{t("วิดีโอที่คุณสร้างล่าสุด", "Your recently generated videos")}</p>
                  </div>
                  <Link to="/history/video">
                    <Button variant="outline" className="gap-2 bg-white/50 border-white/20 hover:bg-white/80">
                      <History className="h-4 w-4" />
                      {t("ดูทั้งหมด", "View All")}
                    </Button>
                  </Link>
                </div>

                {loadingVideos ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                    {recentVideos.map((video) => {
                      // Determine video state and URL
                      let videoUrl = video.result_url || "";
                      if (videoUrl.startsWith("{")) {
                        try {
                          const parsed = JSON.parse(videoUrl);
                          videoUrl = parsed.resultUrls?.[0] || parsed.url || "";
                        } catch {
                          // Keep original if parse fails
                        }
                      } else if (videoUrl.includes(",")) {
                        videoUrl = videoUrl.split(",")[0].trim();
                      }

                      const hasJsonResult = video.result_url?.startsWith("{");
                      const hasHttpResult = video.result_url?.startsWith("http");
                      const hasResult = hasJsonResult || hasHttpResult;
                      // Properly identify if it's completed (status completed AND we have a valid URL)
                      const isCompleted = video.status === "completed" && videoUrl && videoUrl.startsWith("http");
                      const isFailed = video.status === "failed";
                      const canCheckStatus = !isCompleted && !isFailed;

                      return (
                        <Card
                          key={video.id}
                          className="group overflow-hidden border border-white/20 shadow-xl bg-white/80 backdrop-blur-md dark:bg-black/60 hover:bg-white transition-all rounded-3xl"
                        >
                          {/* Video Frame Container */}
                          <div className="p-3">
                            <div className="relative w-full rounded-2xl overflow-hidden shadow-md border border-black/5 bg-black/5">
                              {video.is_professional && (
                                <div className="absolute top-2 left-2 z-10">
                                  <div className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 text-white text-[10px] font-bold shadow-lg">
                                    <Crown className="h-2.5 w-2.5" />
                                    Pro
                                  </div>
                                </div>
                              )}

                              {isCompleted && videoUrl ? (
                                <video
                                  src={videoUrl}
                                  className="w-full h-auto max-h-[600px] block bg-black/10"
                                  controls
                                  playsInline
                                  preload="metadata"
                                />
                              ) : (
                                <div className="w-full aspect-video flex flex-col items-center justify-center bg-gradient-to-br from-primary/10 to-purple-500/10 gap-3">
                                  {isFailed ? (
                                    <>
                                      <AlertTriangle className="h-10 w-10 text-destructive" />
                                      <p className="text-xs text-destructive font-medium">{t("ผิดพลาด", "Failed")}</p>
                                      {video.error_message && (
                                        <p className="text-[10px] text-destructive/70 text-center px-4 line-clamp-3">{video.error_message}</p>
                                      )}
                                    </>
                                  ) : (
                                    <>
                                      <Clock className="h-10 w-10 text-muted-foreground animate-pulse" />
                                      <p className="text-xs text-muted-foreground">{t("กำลังสร้าง...", "Generating...")}</p>
                                      {canCheckStatus && (
                                        <Button
                                          variant="glow"
                                          size="sm"
                                          onClick={() => handleCheckStatus(video.id, "video")}
                                          disabled={checkingStatus === video.id}
                                          className="mt-2"
                                        >
                                          {checkingStatus === video.id ? (
                                            <Loader2 className="h-4 w-4 animate-spin" />
                                          ) : (
                                            <>
                                              <RefreshCw className="h-4 w-4 mr-2" />
                                              {t("เช็กสถานะ", "Check Status")}
                                            </>
                                          )}
                                        </Button>
                                      )}
                                    </>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>

                          <CardContent className="p-5 pt-0 mt-auto">
                            <p className="text-sm font-medium line-clamp-1 text-foreground/90">{getDisplayPrompt(video)}</p>
                            <div className="flex items-center justify-between mt-2">
                              <p className="text-[10px] text-primary/70 font-semibold bg-primary/10 px-1.5 py-0.5 rounded uppercase">
                                {video.model.replace("Video", "").trim()}
                              </p>
                              <div className="flex items-center gap-2">
                                {getStatusBadge(video.status)}
                              </div>
                            </div>

                            <div className="flex items-center justify-between mt-4">
                              <div className="flex flex-col gap-1">
                                <div className="flex items-center gap-3">
                                  <div>
                                    <span className="text-[10px] text-muted-foreground font-bold uppercase tracking-tighter">{t("เมื่อวันที่", "Date")}</span>
                                    <span className="text-xs font-semibold block">{formatDateHistory(video.created_at)}</span>
                                  </div>
                                  {video.credits_used !== undefined && (
                                    <div className="border-l border-white/10 pl-3">
                                      <span className="text-[10px] text-muted-foreground font-bold uppercase tracking-tighter">{t("เครดิต", "Credits")}</span>
                                      {video.status === "failed" ? (
                                        <span className="text-xs font-semibold text-green-500 block">0 ({t("คืนแล้ว", "Refunded")})</span>
                                      ) : (
                                        <span className="text-xs font-semibold text-primary block">-{video.credits_used}</span>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-1">
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="h-8 w-8 text-muted-foreground hover:text-primary rounded-lg transition-colors"
                                  title={video.is_magic ? t("พรอมต์ถูกซ่อน", "Prompt hidden") : t("ใช้พรอมต์ซ้ำ", "Reuse Prompt")}
                                  onClick={() => {
                                    if (video.is_magic) return;
                                    handleReusePrompt(getDisplayPrompt(video));
                                  }}
                                  disabled={video.is_magic}
                                >
                                  <Repeat className="h-4 w-4" />
                                </Button>
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="h-8 w-8 text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded-lg transition-colors"
                                  onClick={() => handleDeleteHistory(video.id, "video")}
                                  title={t("ลบ", "Delete")}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                                {isCompleted && videoUrl && (
                                  <div className="flex gap-1">
                                    <Button
                                      size="icon"
                                      variant="ghost"
                                      className="h-8 w-8 text-primary hover:bg-primary/10 rounded-lg transition-colors relative"
                                      onClick={() => handleDownload(videoUrl, `generated-video-${video.id.slice(0, 8)}.mp4`)}
                                      title={t("ดาวน์โหลด", "Download")}
                                    >
                                      <Download className="h-4 w-4" />
                                    </Button>
                                  </div>
                                )}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </Card>
            </div>
          )
        }
      </main >

      {/* Fullscreen Image Preview */}
      {
        selectedImage && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 p-4 animate-in fade-in duration-200 backdrop-blur-sm"
            onClick={() => setSelectedImage(null)}
          >
            <div className="relative max-w-[95vw] max-h-[95vh] flex items-center justify-center group/modal">
              <img
                src={selectedImage}
                alt="Full screen preview"
                className="max-h-[92vh] max-w-full object-contain rounded-lg shadow-2xl"
              />
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setSelectedImage(null)}
                className="absolute -top-12 right-0 md:top-4 md:right-4 text-white hover:bg-white/20 rounded-full h-10 w-10 bg-black/50"
              >
                <X className="h-6 w-6" />
              </Button>
            </div>
          </div>
        )
      }

      {/* Nano Banana Edit Modal */}
      {
        editingImageUrl && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
            onClick={() => setEditingImageUrl(null)}
          >
            <div
              className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 border-b border-border">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-xl font-bold">{t("แก้ไขภาพด้วย AI", "Edit Image with AI")}</h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      {t("บอก AI ว่าต้องการแก้ไขภาพอย่างไร", "Tell AI how you want to edit the image")}
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setEditingImageUrl(null)}
                    className="rounded-full"
                  >
                    <X className="h-5 w-5" />
                  </Button>
                </div>
              </div>

              <div className="p-6 space-y-4">
                {/* Preview Image */}
                <div className="rounded-xl overflow-hidden border border-border bg-muted/30">
                  <img
                    src={editingImageUrl}
                    alt="Image to edit"
                    className="w-full h-auto max-h-[300px] object-contain"
                  />
                </div>

                {/* Edit Prompt */}
                <div>
                  <label className="text-sm font-medium mb-2 block">
                    {t("ต้องการแก้ไขอย่างไร?", "What would you like to change?")}
                  </label>
                  <Textarea
                    value={editPrompt}
                    onChange={(e) => setEditPrompt(e.target.value)}
                    placeholder={t(
                      "เช่น: เปลี่ยนพื้นหลังเป็นชายหาด, เพิ่มแสงแดด, ลบคนออก...",
                      "e.g.: Change background to beach, add sunlight, remove person..."
                    )}
                    className="min-h-[100px] resize-none"
                  />
                </div>

                {/* Credits Info */}
                <div className="flex items-center gap-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                  <Coins className="h-4 w-4 text-amber-500" />
                  <span className="text-sm">
                    {t(`ใช้ ${NANO_BANANA_EDIT_CREDITS} เครดิต`, `Uses ${NANO_BANANA_EDIT_CREDITS} credits`)}
                  </span>
                  {userCredits < NANO_BANANA_EDIT_CREDITS && (
                    <Badge variant="destructive" className="ml-auto">
                      {t("เครดิตไม่พอ", "Not enough")}
                    </Badge>
                  )}
                </div>
              </div>

              <div className="p-6 border-t border-border flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setEditingImageUrl(null)}
                >
                  {t("ยกเลิก", "Cancel")}
                </Button>
                <Button
                  variant="glow"
                  className="flex-1 gap-2"
                  onClick={handleNanoBananaEdit}
                  disabled={isEditing || !editPrompt.trim() || userCredits < NANO_BANANA_EDIT_CREDITS}
                >
                  {isEditing ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      {t("กำลังแก้ไข...", "Editing...")}
                    </>
                  ) : (
                    <>
                      <Pencil className="h-4 w-4" />
                      {t("แก้ไขภาพ", "Edit Image")}
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        )
      }
    </div >
  );
};

export default Generate;
